/**
 * Utility functions for handling file URLs and paths
 */

/**
 * Return a browser-usable URL for a stored R2 asset.
 *
 * The Cloudflare R2 public host (`https://<hash>.r2.dev`) is already scoped to
 * ONE bucket, so the object key must NOT include the bucket name. Older code
 * used to inject `/main/` into every URL to "fix" a missing bucket segment;
 * that was wrong and produced `object not found` on every fetch.
 *
 * This helper is now defensive:
 *   - passes correct URLs through unchanged
 *   - strips a leading `/main/` from any legacy DB rows that were written with
 *     the redundant bucket segment (so those files render again without a
 *     database backfill).
 *
 * @param {string} url - The stored file URL
 * @returns {string}
 */
export const getCorrectFileUrl = (url) => {
  if (!url) return '';

  // Any r2.dev public URL with a `/main/` bucket segment is malformed —
  // strip it. Matches both `.r2.dev/main/...` and any other pub-host variant.
  return url.replace(/(\.r2\.dev)\/main\//i, '$1/');
};

/**
 * Determines if a file is an image based on its file name or MIME type
 * @param {string} fileName - The name of the file
 * @param {string} fileType - Optional MIME type of the file
 * @returns {boolean} - Whether the file is an image
 */
export const isImageFile = (fileName, fileType = null) => {
  if (fileType && fileType.startsWith('image/')) {
    return true;
  }
  
  if (fileName) {
    const extension = fileName.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension);
  }
  
  return false;
};

/**
 * Determines if a file is a PDF based on its file name or MIME type
 * @param {string} fileName - The name of the file
 * @param {string} fileType - Optional MIME type of the file
 * @returns {boolean} - Whether the file is a PDF
 */
export const isPdfFile = (fileName, fileType = null) => {
  if (fileType && fileType === 'application/pdf') {
    return true;
  }
  
  if (fileName) {
    const extension = fileName.split('.').pop().toLowerCase();
    return extension === 'pdf';
  }
  
  return false;
}; 